import { useChat as useChatV5 } from "@ai-sdk/react";
import { DefaultChatTransport } from "ai";
import {
  ensureUiMessages,
  toLegacyMessage,
  toUiMessage,
} from "@/lib/ai-message-utils";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

// How often a streaming reply re-renders, at most.
const STREAM_RENDER_THROTTLE_MS = 50;

export function useChat(options = {}) {
  const {
    api = "/api/chat",
    body,
    credentials,
    headers,
    // The only hook onto the raw Response. `chat.sendMessage` resolves to
    // nothing, so a caller that needs a response header (the multi-persona
    // speaker echo) cannot get it from `onResponse` — it has to wrap fetch.
    fetch: fetchImpl,
    transport,
    initialMessages = [],
    messages,
    onFinish,
    onResponse,
    onError,
    ...rest
  } = options;

  const bodyRef = useRef();
  const latestBody = typeof body === "function" ? body() : body;
  bodyRef.current = latestBody;

  const transportInstance = useMemo(() => {
    if (transport) {
      return transport;
    }

    return new DefaultChatTransport({
      api,
      credentials,
      headers,
      ...(fetchImpl ? { fetch: fetchImpl } : {}),
      body: () => bodyRef.current,
    });
  }, [transport, api, credentials, headers, fetchImpl]);

  const [input, setInput] = useState("");

  const handleInputChange = useCallback((event) => {
    const value = event?.target?.value ?? "";
    setInput(value);
  }, []);

  const chatOptions = useMemo(() => {
    const baseMessages = messages ?? initialMessages;

    return {
      // One render per streamed token re-rendered the page and the side nav
      // that shows the chat (SideNavSlots) dozens of times a second, which
      // tripped React's "Maximum update depth exceeded" mid-reply. A caller
      // may pass its own.
      experimental_throttle: STREAM_RENDER_THROTTLE_MS,
      ...rest,
      transport: transportInstance,
      messages: ensureUiMessages(baseMessages),
      onError,
      onFinish: (payload) => {
        if (typeof onFinish === "function" && payload?.message) {
          onFinish(toLegacyMessage(payload.message), payload);
        }
      },
    };
  }, [messages, initialMessages, rest, transportInstance, onError, onFinish]);

  const chat = useChatV5(chatOptions);

  const previousInitialMessagesRef = useRef(
    JSON.stringify(initialMessages ?? [])
  );

  useEffect(() => {
    const signature = JSON.stringify(initialMessages ?? []);

    if (previousInitialMessagesRef.current !== signature) {
      previousInitialMessagesRef.current = signature;
      chat.setMessages(ensureUiMessages(initialMessages));
    }
  }, [initialMessages, chat.setMessages]);

  const legacyMessages = useMemo(
    () => chat.messages.map((message) => toLegacyMessage(message)),
    [chat.messages]
  );

  const send = useCallback(
    async (message, requestOptions) => {
      const baseMessage =
        typeof message === "string"
          ? { role: "user", content: message }
          : message;

      const uiMessage = toUiMessage(baseMessage);
      const mergedBody =
        requestOptions && "body" in requestOptions
          ? requestOptions.body
          : bodyRef.current;

      const response = await chat.sendMessage(uiMessage, {
        ...requestOptions,
        body: mergedBody,
      });

      if (typeof onResponse === "function" && response) {
        onResponse(response);
      }

      return response;
    },
    [chat, onResponse]
  );

  const append = useCallback(
    async (message, requestOptions) => send(message, requestOptions),
    [send]
  );

  const handleSubmit = useCallback(
    async (event, requestOptions) => {
      if (event?.preventDefault) {
        event.preventDefault();
      }

      const value = input.trim();
      if (!value) {
        return;
      }

      setInput("");
      await send({ role: "user", content: value }, requestOptions);
    },
    [input, send]
  );

  const setMessages = useCallback(
    (nextMessages) => chat.setMessages(ensureUiMessages(nextMessages)),
    [chat]
  );

  const sendMessage = useCallback(
    async (message, requestOptions) => send(message, requestOptions),
    [send]
  );

  // The SDK's in-flight statuses: waiting for the first token, then reading.
  const isLoading = chat.status === "submitted" || chat.status === "streaming";

  return {
    ...chat,
    messages: legacyMessages,
    append,
    sendMessage,
    setMessages,
    input,
    setInput,
    handleInputChange,
    handleSubmit,
    isLoading,
  };
}
